import React, { useEffect, useState } from "react";
import {
  Wallet as WalletIcon, AlertTriangle, ShieldCheck, ShieldAlert, Save,
  CreditCard, Settings as SettingsIcon, RefreshCw, Trash2, Plus, Minus, Eye, X,
} from "lucide-react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { formatINR } from "../../lib/utils.fmt";
import EmptyState from "../../components/EmptyState";
import { toast } from "sonner";

export default function SuperTenantWallets() {
  const [wallets, setWallets] = useState([]);
  const [billing, setBilling] = useState({ otp_charge: 0.3, block_below: -50, low_balance: 50, bill_share_charge: 0.3 });
  const [rzp, setRzp] = useState({ configured: false, live: false, webhook_configured: false, key_id_masked: "", key_secret_masked: "", webhook_secret_masked: "" });
  const [rzpForm, setRzpForm] = useState({ key_id: "", key_secret: "", webhook_secret: "" });
  const [active, setActive] = useState(null);   // open adjust dialog for tenant
  const [activeTxns, setActiveTxns] = useState([]);
  const [adjAmount, setAdjAmount] = useState(0);
  const [adjNote, setAdjNote] = useState("Manual adjustment");
  const [busy, setBusy] = useState(false);

  const loadAll = async () => {
    const [w, b, r] = await Promise.all([
      api.get("/api/super-admin/tenant-wallets"),
      api.get("/api/super-admin/billing-settings"),
      api.get("/api/super-admin/settings/razorpay"),
    ]);
    setWallets(w.data.wallets);
    setBilling(b.data);
    setRzp(r.data);
  };
  useEffect(() => { loadAll(); }, []);

  const saveBilling = async () => {
    setBusy(true);
    try {
      await api.put("/api/super-admin/billing-settings", billing);
      toast.success("Billing settings saved — applies to all new OTPs");
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const saveRzp = async () => {
    setBusy(true);
    try {
      const body = Object.fromEntries(Object.entries(rzpForm).filter(([, v]) => v && v.trim()));
      if (Object.keys(body).length === 0) { toast.error("Nothing to save"); return; }
      await api.put("/api/super-admin/settings/razorpay", body);
      toast.success("Razorpay keys saved");
      setRzpForm({ key_id: "", key_secret: "", webhook_secret: "" });
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const clearRzp = async () => {
    if (!window.confirm("Clear all Razorpay keys? Tenants won't be able to recharge.")) return;
    await api.delete("/api/super-admin/settings/razorpay");
    toast.success("Razorpay keys cleared");
    loadAll();
  };

  const openAdjust = async (t) => {
    setActive(t);
    setAdjAmount(0); setAdjNote("Manual adjustment");
    const { data } = await api.get(`/api/super-admin/tenant-wallets/${t.id}/transactions`);
    setActiveTxns(data.transactions);
  };

  const adjust = async (sign = 1) => {
    if (!active || !adjAmount) return;
    setBusy(true);
    try {
      await api.post(`/api/super-admin/tenant-wallets/${active.id}/adjust`, {
        amount: sign * Math.abs(parseFloat(adjAmount)),
        note: adjNote || "Manual adjustment",
      });
      toast.success("Wallet updated");
      setActive(null); setAdjAmount(0);
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 page-enter" data-testid="super-wallets-page">
      <div>
        <div className="text-xs uppercase tracking-wider font-bold text-neutral-500">Platform Billing</div>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Tenant wallets & OTP charges</h1>
        <p className="text-sm text-neutral-600 mt-1">
          Every customer OTP debits the tenant's platform wallet. You set the charge here.
        </p>
      </div>

      {/* OTP charge settings */}
      <section className="bg-white rounded-3xl border border-neutral-200 p-6">
        <div className="flex items-center gap-3 mb-5">
          <SettingsIcon className="w-5 h-5 text-brand-sky"/>
          <h2 className="font-heading font-bold text-lg">OTP pricing</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <NumberField label="₹ per OTP" value={billing.otp_charge}
            onChange={(v) => setBilling({ ...billing, otp_charge: parseFloat(v) || 0 })}
            hint="Charged to tenant on every customer OTP sent" testId="setting-otp-charge"/>
          <NumberField label="Low-balance alert at ₹" value={billing.low_balance}
            onChange={(v) => setBilling({ ...billing, low_balance: parseFloat(v) || 0 })}
            hint="Shows warning banner in tenant admin" testId="setting-low-balance"/>
          <NumberField label="Block OTPs below ₹" value={billing.block_below}
            onChange={(v) => setBilling({ ...billing, block_below: parseFloat(v) || 0 })}
            hint="Grace overdraft — use negative number (e.g. -50)" testId="setting-block-below"/>
          <NumberField label="₹ per bill-share message" value={billing.bill_share_charge}
            onChange={(v) => setBilling({ ...billing, bill_share_charge: parseFloat(v) || 0 })}
            hint="Charged per SMS/WhatsApp bill notification sent" testId="setting-bill-share-charge"/>
        </div>
        <Button onClick={saveBilling} disabled={busy} className="h-12 mt-5 rounded-2xl bg-brand-sky hover:bg-blue-700 font-bold px-7" data-testid="save-billing-btn">
          <Save className="w-4 h-4 mr-2"/> Save pricing
        </Button>
      </section>

      {/* Automatic Bill Sharing is now a per-tenant switch (super admin) —
          see the "Automatic Bill Sharing" toggle on each tenant's detail
          panel in Tenants, alongside Order Editing / Bottle Tracking. */}

      {/* Razorpay keys */}
      <section className="bg-white rounded-3xl border border-neutral-200 p-6">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <CreditCard className="w-5 h-5 text-brand-sky"/>
            <h2 className="font-heading font-bold text-lg">Razorpay (tenant recharge)</h2>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {rzp.configured ? (
              <span className="px-3 py-1.5 rounded-full text-xs font-bold inline-flex items-center gap-1.5 bg-brand-leaf/10 text-brand-leaf uppercase tracking-wider" data-testid="rzp-status-pill">
                <ShieldCheck className="w-3.5 h-3.5"/> Configured
              </span>
            ) : (
              <span className="px-3 py-1.5 rounded-full text-xs font-bold inline-flex items-center gap-1.5 bg-brand-sand/15 text-brand-sand uppercase tracking-wider" data-testid="rzp-status-pill">
                <ShieldAlert className="w-3.5 h-3.5"/> Not configured — recharges fall back to mock
              </span>
            )}
            {rzp.configured && (
              <span className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider ${rzp.live ? "bg-brand-leaf/10 text-brand-leaf" : "bg-amber-100 text-amber-700"}`}
                    data-testid="rzp-mode-pill">
                {rzp.live ? "Live mode — real charges" : "Test mode"}
              </span>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Key ID">
            <Input value={rzpForm.key_id} onChange={(e) => setRzpForm({ ...rzpForm, key_id: e.target.value })}
                   placeholder={rzp.key_id_masked || "rzp_test_xxxxxxxxxxxxxx"} className="h-12 rounded-2xl font-mono text-sm"
                   data-testid="rzp-key-id"/>
          </Field>
          <Field label="Key Secret">
            <Input type="password" value={rzpForm.key_secret} onChange={(e) => setRzpForm({ ...rzpForm, key_secret: e.target.value })}
                   placeholder={rzp.key_secret_masked || "••••••••••••••••"} className="h-12 rounded-2xl font-mono text-sm"
                   data-testid="rzp-key-secret"/>
          </Field>
          <Field label="Webhook Secret (optional)">
            <Input type="password" value={rzpForm.webhook_secret} onChange={(e) => setRzpForm({ ...rzpForm, webhook_secret: e.target.value })}
                   placeholder={rzp.webhook_secret_masked || "whsec_xxxxx"} className="h-12 rounded-2xl font-mono text-sm"
                   data-testid="rzp-webhook-secret"/>
          </Field>
        </div>
        <div className="flex gap-3 mt-5 flex-wrap">
          <Button onClick={saveRzp} disabled={busy} className="h-12 rounded-2xl bg-brand-sky hover:bg-blue-700 font-bold px-7" data-testid="save-rzp-btn">
            <Save className="w-4 h-4 mr-2"/> Save Razorpay keys
          </Button>
          {rzp.configured && (
            <Button onClick={clearRzp} variant="outline" className="h-12 rounded-2xl border-2 border-brand-clay text-brand-clay font-bold" data-testid="clear-rzp-btn">
              <Trash2 className="w-4 h-4 mr-2"/> Clear keys
            </Button>
          )}
        </div>
        <p className="text-xs text-neutral-500 mt-3">
          Get keys from <a href="https://dashboard.razorpay.com/app/keys" target="_blank" rel="noreferrer" className="underline text-brand-sky">dashboard.razorpay.com/app/keys</a>.
          Same Razorpay account services every tenant recharge. Use a <code className="font-mono">rzp_test_</code> key while testing.
        </p>
        <p className="text-xs text-neutral-500 mt-2">
          Webhook (optional, catches recharges whose success callback never reached the app): in Razorpay Dashboard → Webhooks, add{" "}
          <code className="font-mono break-all">{`${process.env.REACT_APP_BACKEND_URL}/api/public/razorpay/webhook`}</code>, subscribe to <code className="font-mono">payment.captured</code>,
          and paste the secret above. {rzp.webhook_configured && <span className="text-brand-leaf font-semibold">Currently configured.</span>}
        </p>
      </section>

      {/* Tenant balances */}
      <section className="bg-white rounded-3xl border border-neutral-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-neutral-200 flex items-center justify-between">
          <div>
            <h2 className="font-heading font-bold">Tenant balances ({wallets.length})</h2>
            <p className="text-xs text-neutral-500">Sorted by lowest balance first</p>
          </div>
          <button onClick={loadAll} className="text-sm text-brand-sky font-bold inline-flex items-center gap-1" data-testid="refresh-wallets">
            <RefreshCw className="w-4 h-4"/> Refresh
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px]">
            <thead className="bg-neutral-50 text-xs uppercase tracking-wider font-bold text-neutral-500">
              <tr>
                <th className="text-left px-5 py-2.5">Tenant</th>
                <th className="text-left px-5 py-2.5">Slug</th>
                <th className="text-right px-5 py-2.5">Balance</th>
                <th className="text-left px-5 py-2.5">Status</th>
                <th className="text-right px-5 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {wallets.map((w) => {
                const low = w.balance < (w.low_balance_threshold ?? 50);
                const blocked = w.balance < (w.block_threshold ?? -50);
                return (
                  <tr key={w.id} className="hover:bg-neutral-50" data-testid={`wallet-row-${w.slug}`}>
                    <td className="px-5 py-2.5 font-semibold">{w.name}</td>
                    <td className="px-5 py-2.5 text-sm font-mono text-neutral-500">{w.slug}</td>
                    <td className={`px-5 py-2.5 text-right font-mono font-bold ${blocked ? "text-brand-clay" : low ? "text-brand-sand" : ""}`}>
                      {formatINR(w.balance)}
                    </td>
                    <td className="px-5 py-2.5">
                      {blocked ? <span className="px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wider bg-brand-clay/10 text-brand-clay inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3"/> Blocked</span>
                       : low ? <span className="px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wider bg-brand-sand/10 text-brand-sand">Low</span>
                       : <span className="px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wider bg-brand-leaf/10 text-brand-leaf">Healthy</span>}
                    </td>
                    <td className="px-5 py-2.5 text-right">
                      <button onClick={() => openAdjust(w)} className="text-sm text-brand-sky font-bold inline-flex items-center gap-1" data-testid={`adjust-wallet-${w.slug}`}>
                        <WalletIcon className="w-4 h-4"/> Adjust
                      </button>
                    </td>
                  </tr>
                );
              })}
              {wallets.length === 0 && <tr><td colSpan="5"><EmptyState icon={WalletIcon} size="sm" title="No tenants" /></td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-lg rounded-3xl">
          <DialogHeader><DialogTitle className="font-heading text-2xl">{active?.name} · Wallet</DialogTitle></DialogHeader>
          {active && (
            <>
              <div className="rounded-2xl bg-brand-cream p-4">
                <div className="text-xs uppercase tracking-wider font-bold text-neutral-500">Current balance</div>
                <div className="font-heading text-3xl font-extrabold">{formatINR(active.balance)}</div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-bold text-neutral-500">Amount (₹)</label>
                <Input type="number" value={adjAmount} onChange={(e) => setAdjAmount(e.target.value)}
                       placeholder="e.g. 500" className="h-12 rounded-2xl mt-1" data-testid="adjust-amount-input"/>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-bold text-neutral-500">Note</label>
                <Input value={adjNote} onChange={(e) => setAdjNote(e.target.value)}
                       className="h-12 rounded-2xl mt-1" data-testid="adjust-note-input"/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Button onClick={() => adjust(1)} disabled={busy || !adjAmount}
                        className="h-12 rounded-2xl bg-brand-leaf hover:bg-emerald-700 font-bold" data-testid="credit-btn">
                  <Plus className="w-4 h-4 mr-2"/> Credit
                </Button>
                <Button onClick={() => adjust(-1)} disabled={busy || !adjAmount}
                        className="h-12 rounded-2xl bg-brand-clay hover:bg-red-700 font-bold" data-testid="debit-btn">
                  <Minus className="w-4 h-4 mr-2"/> Debit
                </Button>
              </div>
              <div className="mt-4 max-h-64 overflow-y-auto rounded-2xl border border-neutral-200">
                <table className="w-full text-xs">
                  <thead className="bg-neutral-50 text-[10px] uppercase tracking-wider font-bold text-neutral-500">
                    <tr><th className="text-left px-3 py-2">When</th><th className="text-left px-3 py-2">Type</th>
                        <th className="text-right px-3 py-2">Amount</th><th className="text-right px-3 py-2">Balance</th></tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-100">
                    {activeTxns.slice(0, 50).map((t) => (
                      <tr key={t.id}>
                        <td className="px-3 py-1.5 font-mono">{t.created_at?.slice(0, 16).replace("T", " ")}</td>
                        <td className="px-3 py-1.5">{t.type}</td>
                        <td className={`px-3 py-1.5 text-right font-mono font-bold ${t.amount >= 0 ? "text-brand-leaf" : "text-brand-clay"}`}>
                          {t.amount >= 0 ? "+" : ""}{formatINR(t.amount)}
                        </td>
                        <td className="px-3 py-1.5 text-right font-mono">{formatINR(t.balance_after)}</td>
                      </tr>
                    ))}
                    {activeTxns.length === 0 && <tr><td colSpan="4"><EmptyState icon={WalletIcon} size="sm" title="No transactions" className="py-4" /></td></tr>}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider font-bold text-neutral-500 mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}

function NumberField({ label, value, onChange, hint, testId }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider font-bold text-neutral-500 mb-1.5 block">{label}</label>
      <Input type="number" step="0.01" value={value} onChange={(e) => onChange(e.target.value)}
             className="h-12 rounded-2xl font-mono" data-testid={testId}/>
      {hint && <div className="text-[11px] text-neutral-500 mt-1">{hint}</div>}
    </div>
  );
}
